import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError, SearchParams } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  CommissionPlansCreateSchema,
  CommissionPlansQueryAllSchema,
  CommissionPlansQuerySchema,
  CommissionPlansUpdateSchema,
} from './schemas';
import type {
  CommissionTiersCreateSchema,
  CommissionTiersFormSchema,
  CommissionTiersMutationSchema,
} from './schemas/commission-tiers';

export function useGetCommissionPlansControlled({ per_page }: SearchParams<{ per_page: number }>, enabled = true) {
  return useQuery({
    queryKey: ['COMMISSION_PLANS', per_page],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<CommissionPlansQueryAllSchema>({
        endpoint: '/api/v1/commission-plans',
        params: { per_page },
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['COMMISSION_PLANS'] });
};

export const useGetCommissionPlansMutation: UseControlledMutation<CommissionPlansQuerySchema> = (params = {}) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['COMMISSION_PLANS_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<CommissionPlansQueryAllSchema>({
            endpoint: '/api/v1/commission-plans',
            params: { search, per_page },
          });
          return res?.data || [];
        },
      });
    },
  });

export function useGetCommissionPlans(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['COMMISSION_PLANS', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<CommissionPlansQueryAllSchema>({
        endpoint: '/api/v1/commission-plans?include=commission_tiers',
        params: apiParamStr,
      });

      return res; // { ...res, data: fakeCommissionPlans };
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, CommissionPlansCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-plans/',
        payload,
      }),
  });

export function useGetCommissionPlan(id: string) {
  return useSuspenseQuery({
    queryKey: ['COMMISSION_PLAN', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: CommissionPlansQuerySchema }>({
        endpoint: `/api/v1/commission-plans/${id}?include=commission_tiers`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: CommissionPlansQuerySchema }, ApiError, Partial<CommissionPlansUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/commission-plans/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['COMMISSION_PLAN_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/commission-plans/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });

export const useCommissionTierBulkCreateMutation = () =>
  useMutation<CommissionTiersFormSchema[], ApiError, { items: CommissionTiersCreateSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-tiers/bulk-store',
        payload,
      }),
  });

export const useCommissionTierBulkUpdateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, { items: CommissionTiersMutationSchema[] }>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-tiers/bulk-update',
        payload,
      }),
  });

export const useCommissionTierBulkDeleteMutation = () =>
  useMutation<
    { data: { id: number } },
    ApiError,
    {
      items: number[];
    }
  >({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/commission-tiers/bulk-delete',
        payload,
      }),
  });
